import { z } from 'zod';
const absentWhenNull = (schema) => z.preprocess((value) => (value === null ? undefined : value), schema.optional());
const decimalPattern = (integerDigits, fractionDigits) => new RegExp(`^(?:0|[1-9]\\d{0,${integerDigits - 1}})(?:\\.\\d{1,${fractionDigits}})?$`);
export const loanDecimalSchema = z
    .string()
    .regex(decimalPattern(16, 2), 'Use a decimal with up to 16 integer and 2 fractional digits');
export const signedLoanDecimalSchema = z
    .string()
    .regex(/^-?(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/, 'Use a signed decimal with up to 16 integer and 2 fractional digits');
export const loanRateSchema = z
    .string()
    .regex(decimalPattern(1, 8), 'Use a rate between 0 and 1 with up to 8 fractional digits')
    .refine((value) => {
    const [integer, fraction = ''] = value.split('.');
    return integer === '0' || (integer === '1' && /^0*$/.test(fraction));
}, 'Rate must not exceed 1');
export const positiveLoanDecimalSchema = loanDecimalSchema.refine((value) => {
    const match = /^(\d+)(?:\.(\d+))?$/.exec(value);
    if (!match)
        return true;
    const cents = BigInt(match[1]) * 100n + BigInt((match[2] ?? '').padEnd(2, '0'));
    return cents > 0n && cents <= 1000000000000n;
}, 'Amount must be between $0.01 and $10,000,000,000.00');
export const loanDateSchema = z.iso.date();
export const publicDisplayIdSchema = z.string().regex(/^[A-Z]{4}[23456789A-HJ-KM-NP-Z]{8}$/);
export const previewDigestSchema = z.string().regex(/^[a-f0-9]{64}$/);
const loanTermsShape = {
    fundDisplayId: absentWhenNull(publicDisplayIdSchema),
    ownershipDisplayId: publicDisplayIdSchema.optional(),
    lender: z.string().trim().min(1).max(200),
    amount: positiveLoanDecimalSchema,
    annualNominalRate: loanRateSchema,
    annualPercentageRate: absentWhenNull(loanRateSchema),
    startDate: loanDateSchema,
    termMonths: z.number().int().min(1).max(600),
    endDate: loanDateSchema.optional(),
};
const normalizeNullEndDate = (value) => {
    if (typeof value !== 'object' ||
        value === null ||
        !('endDate' in value) ||
        value.endDate !== null)
        return value;
    const { endDate: _endDate, ...withoutNull } = value;
    return withoutNull;
};
function requireLoanOwnership(value, context) {
    const { fundDisplayId, ownershipDisplayId, ...terms } = value;
    if (fundDisplayId !== undefined) {
        return {
            ...terms,
            fundDisplayId,
            ...(ownershipDisplayId === undefined ? {} : { ownershipDisplayId }),
        };
    }
    if (ownershipDisplayId !== undefined)
        return { ...terms, ownershipDisplayId };
    context.addIssue({
        code: 'custom',
        path: ['ownershipDisplayId'],
        message: 'Select an ownership for the loan.',
    });
    return z.NEVER;
}
export const loanTermsSchema = z.preprocess(normalizeNullEndDate, z.object(loanTermsShape).transform(requireLoanOwnership));
export const LOAN_FISCAL_YEAR_MIN = 1900;
export const LOAN_FISCAL_YEAR_MAX = 2200;
export const loanListInputSchema = z.object({
    ownershipDisplayId: z.string().min(1),
    fiscalYear: z.number().int().min(LOAN_FISCAL_YEAR_MIN).max(LOAN_FISCAL_YEAR_MAX),
});
export const loanScheduleRowSchema = z.object({
    rowNumber: z.number().int().positive(),
    dueDate: loanDateSchema,
    openingBalance: loanDecimalSchema,
    paymentAmount: loanDecimalSchema,
    principalAmount: loanDecimalSchema,
    interestAmount: signedLoanDecimalSchema,
    closingBalance: loanDecimalSchema,
});
export const loanScheduleTotalsSchema = z.object({
    paymentAmount: loanDecimalSchema,
    principalAmount: loanDecimalSchema,
    interestAmount: signedLoanDecimalSchema,
});
export const schedulePreviewSchema = z
    .object({
    calculationArtifactId: z.string().min(1),
    previewDigest: previewDigestSchema,
    rows: z.array(loanScheduleRowSchema).min(1),
    totals: loanScheduleTotalsSchema,
})
    .superRefine((value, context) => {
    value.rows.forEach((row, index) => {
        if (row.rowNumber !== index + 1) {
            context.addIssue({
                code: 'custom',
                path: ['rows', index, 'rowNumber'],
                message: 'Rows must be ordered and contiguous',
            });
        }
    });
});
export const loanSavePendingInputSchema = z.preprocess(normalizeNullEndDate, z
    .object({
    ...loanTermsShape,
    idempotencyKey: z.uuid(),
    previewDigest: previewDigestSchema,
})
    .transform(requireLoanOwnership));
export const loanSavePendingOutputSchema = z.object({
    loanDisplayId: publicDisplayIdSchema,
    scheduleVersionDisplayId: publicDisplayIdSchema,
    status: z.literal('pending'),
    revision: z.number().int().nonnegative(),
});
export const loanGetOutputSchema = z.object({
    loanDisplayId: publicDisplayIdSchema,
    scheduleVersionDisplayId: publicDisplayIdSchema,
    status: z.enum(['pending', 'approved']),
    revision: z.number().int().nonnegative(),
    terms: loanTermsSchema,
    schedule: schedulePreviewSchema,
    lifecycleSummary: z.array(z.object({ action: z.string(), occurredAt: z.iso.datetime() })),
});
export const loanCreateCorrectionInputSchema = z.object({
    loanDisplayId: publicDisplayIdSchema,
    baseScheduleVersionDisplayId: publicDisplayIdSchema,
    expectedRevision: z.number().int().nonnegative(),
    correctedValues: loanTermsSchema,
    previewDigest: previewDigestSchema,
    idempotencyKey: z.uuid(),
});
export const loanDeletePendingInputSchema = z.object({
    loanDisplayId: publicDisplayIdSchema,
    scheduleVersionDisplayId: publicDisplayIdSchema,
    expectedRevision: z.number().int().nonnegative(),
});
export const loanDeletePendingOutputSchema = z.object({
    deleted: z.literal(true),
    loanDisplayId: publicDisplayIdSchema,
    deletedScheduleVersionDisplayId: publicDisplayIdSchema,
    revision: z.number().int().nonnegative(),
});
export const loanApproveInputSchema = z
    .object({
    loanDisplayId: publicDisplayIdSchema,
    scheduleVersionDisplayId: publicDisplayIdSchema,
})
    .strict();
export const loanApproveOutputSchema = z.object({
    status: z.literal('approved'),
    scheduleVersionDisplayId: publicDisplayIdSchema,
    commitmentDisplayId: publicDisplayIdSchema.optional(),
    eligibleInterestRows: z.number().int().nonnegative(),
    settled: z.literal(true),
    revision: z.number().int().nonnegative(),
});
export const loanErrorCodeSchema = z.enum([
    'INVALID_INPUT',
    'FUND_NOT_FOUND',
    'FORBIDDEN',
    'CALCULATION_FAILED',
    'CALCULATION_RUNTIME_UNSUPPORTED',
    'APPROVAL_PERMISSION_DENIED',
    'TENANT_ACCESS_DENIED',
    'SCHEDULE_VERSION_NOT_CURRENT',
    'PENDING_VERSION_NOT_FOUND',
    'APPROVED_VERSION_IMMUTABLE',
    'CONCURRENT_STATE_SETTLED',
    'APPROVAL_TRANSACTION_FAILED',
    'APPROVAL_FAILED',
    'STALE_PREVIEW',
    'CONFLICT',
    'LOAN_NOT_FOUND',
    'PREVIEW_FAILED',
    'SAVE_FAILED',
    'READ_FAILED',
]);
export const loanActionSchema = z.enum([
    'preview',
    'savePending',
    'get',
    'createCorrection',
    'deletePending',
    'approve',
]);
export const loanFieldErrorsSchema = z.record(z.string(), z.array(z.string()));
export const loanSafeErrorSchema = z.object({
    code: loanErrorCodeSchema,
    message: z.string(),
    action: loanActionSchema,
    retryable: z.boolean(),
    settled: z.boolean(),
    fieldErrors: loanFieldErrorsSchema.optional(),
    correlationId: z.string().min(1),
});
export function loanCents(value) {
    const negative = value.startsWith('-');
    const [whole, fraction = ''] = (negative ? value.slice(1) : value).split('.');
    const amount = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0').slice(0, 2));
    return negative ? -amount : amount;
}
export function formatLoanCents(value) {
    const absolute = value < 0n ? -value : value;
    return `${value < 0n ? '-' : ''}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
}
export const sumLoanAmounts = (values) => formatLoanCents(values.reduce((total, value) => total + loanCents(value), 0n));
//# sourceMappingURL=loans.js.map